const roundDays = (value) => Math.round((Number(value) || 0) * 100) / 100;

const parseDateInput = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const segments = value.split('-');
  if (segments.length !== 3) {
    return null;
  }

  const [yearRaw, monthRaw, dayRaw] = segments;
  const year = Number.parseInt(yearRaw, 10);
  const month = Number.parseInt(monthRaw, 10);
  const day = Number.parseInt(dayRaw, 10);

  if ([year, month, day].some((part) => Number.isNaN(part))) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const isWeekend = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }
  const day = date.getDay();
  return day === 0 || day === 6;
};

export const calculateVacationDaysByYear = (
  startInput,
  endInput,
  dayPart = 'full-day',
) => {
  const start = startInput instanceof Date ? startInput : parseDateInput(startInput);
  const end = endInput instanceof Date ? endInput : parseDateInput(endInput);

  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return {};
  }

  if (end.getTime() < start.getTime()) {
    return {};
  }

  const breakdown = {};
  const normalizedDayPart = typeof dayPart === 'string' ? dayPart : 'full-day';
  const sameDay = start.toDateString() === end.toDateString();
  const isHalfDay = normalizedDayPart !== 'full-day' && sameDay;

  if (isHalfDay) {
    if (isWeekend(start)) {
      return {};
    }
    const yearKey = start.getFullYear().toString();
    breakdown[yearKey] = 0.5;
    return breakdown;
  }

  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endCursor = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (cursor.getTime() <= endCursor.getTime()) {
    if (!isWeekend(cursor)) {
      const yearKey = cursor.getFullYear().toString();
      const nextValue = roundDays((breakdown[yearKey] ?? 0) + 1);
      breakdown[yearKey] = nextValue;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return breakdown;
};

export const sumVacationDays = (breakdown) =>
  roundDays(
    Object.values(breakdown ?? {}).reduce(
      (total, value) => (Number.isFinite(Number(value)) ? total + Number(value) : total),
      0,
    ),
  );

export const normalizeRemainingVacationDays = (record) => {
  if (!record || typeof record !== 'object') {
    return {};
  }

  const entries = Array.isArray(record) ? record : Object.entries(record);
  return entries.reduce((acc, entry) => {
    if (!Array.isArray(entry)) {
      return acc;
    }
    const [rawYear, rawValue] = entry;
    const yearKey = String(rawYear ?? '').trim();
    if (!yearKey) {
      return acc;
    }

    let numericValue;
    if (typeof rawValue === 'number') {
      numericValue = rawValue;
    } else if (typeof rawValue === 'string') {
      numericValue = Number(rawValue);
    } else if (
      rawValue &&
      typeof rawValue === 'object' &&
      typeof rawValue.toString === 'function'
    ) {
      numericValue = Number(rawValue.toString());
    } else {
      numericValue = NaN;
    }

    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return acc;
    }

    acc[yearKey] = roundDays(numericValue);
    return acc;
  }, {});
};

export const formatVacationDays = (value) => {
  if (!Number.isFinite(Number(value))) {
    return '0 days';
  }
  const rounded = roundDays(value);
  const display = Number.isInteger(rounded) ? `${rounded}` : `${rounded}`;
  return `${display} day${Math.abs(rounded) === 1 ? '' : 's'}`;
};

export const formatVacationBreakdown = (breakdown) => {
  if (!breakdown || typeof breakdown !== 'object') {
    return '';
  }

  const segments = Object.entries(breakdown)
    .filter(([, value]) => Number.isFinite(Number(value)) && Number(value) > 0)
    .sort(([a], [b]) => Number.parseInt(a, 10) - Number.parseInt(b, 10))
    .map(([year, value]) => `${year}: ${formatVacationDays(value)}`);

  return segments.join(', ');
};

export const getYearFromDateInput = (value) => {
  const date = parseDateInput(value);
  return date ? date.getFullYear().toString() : null;
};

export default {
  calculateVacationDaysByYear,
  sumVacationDays,
  normalizeRemainingVacationDays,
  formatVacationDays,
  formatVacationBreakdown,
  getYearFromDateInput,
};
